import {
  BASE_PATH,
  LOCAL_LANDING_PAGE_PATH,
  RESULT_PATH,
  RESULT_PATH_IV24,
  VALUATION_FUNNEL_PATH,
  VALUATION_FUNNEL_PATH_IV24,
} from '@/router/routes/constants';
import { Pages } from '@/types';
import { HgSupportedLanguage } from '@smg-real-estate/domus';

export const normalizeAppPath = (path: string): string => {
  const basePath = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');

  if (!basePath || basePath === '/' || !path.startsWith(basePath)) {
    return path;
  }

  const normalizedPath = path.slice(basePath.length);
  return normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
};

export const isTargetPageByPath = (
  path: string,
  routes: Record<HgSupportedLanguage, string>,
  dynamicParams = false,
): boolean => {
  const valuationPaths = Object.values(routes).map((route) => {
    // Extract the inline constraint (e.g. `[\w\-]+` from `:location([\w\-]+)`) when
    // dynamicParams is true, so the helper honours the same slug rules as the router.
    const regexPath = dynamicParams
      ? route.replace(/:([^(/]+)(?:\(([^)]+)\))?/g, (_, _name, constraint) => constraint ?? '[^/]+')
      : route;
    return new RegExp(`^${regexPath.replace(/\//g, '\\/')}\\/?$`);
  });

  return valuationPaths.some((regex) => regex.test(path));
};

export const getLoginCallbackUrl = (includeOrigin = true): string => {
  const path = `/en/property-valuation/login-callback`;
  if (includeOrigin) return `${window.location.origin}${path}`;
  return path;
};

export const isFunnelPage = (path: string): boolean => {
  return isTargetPageByPath(path, VALUATION_FUNNEL_PATH);
};

export const isLandingPage = (path: string): boolean => {
  return isTargetPageByPath(path, BASE_PATH);
};

export const isLocalLandingPage = (path: string): boolean => {
  return isTargetPageByPath(path, LOCAL_LANDING_PAGE_PATH, true);
};

export const isResultPage = (path: string): boolean => {
  return isTargetPageByPath(path, RESULT_PATH_IV24);
};

export const isIV24FunnelPage = (path: string): boolean => {
  return isTargetPageByPath(path, VALUATION_FUNNEL_PATH_IV24);
};

export const isValuationFunnelPage = (path: string): boolean => {
  return isFunnelPage(path) || isIV24FunnelPage(path);
};

export const isResultPath = (path: string): boolean => {
  return isTargetPageByPath(path, RESULT_PATH) || isTargetPageByPath(path, RESULT_PATH_IV24);
};

export const isLeavingValuationFunnel = (destinationPath: string): boolean => {
  const normalizedPath = normalizeAppPath(destinationPath);

  if (isResultPath(normalizedPath)) {
    return false;
  }

  return !isValuationFunnelPage(normalizedPath);
};

export const isExternalFunnelNavigation = (destinationUrl: URL, currentUrl: URL): boolean => {
  const destinationPath = normalizeAppPath(destinationUrl.pathname);
  const currentPath = normalizeAppPath(currentUrl.pathname);

  if (!isLeavingValuationFunnel(destinationPath)) {
    return false;
  }

  return !(
    destinationPath === currentPath &&
    destinationUrl.search === currentUrl.search &&
    destinationUrl.hash === currentUrl.hash
  );
};

export const isIV24ResultPage = (path: string): boolean => {
  return isTargetPageByPath(path, RESULT_PATH_IV24);
};

export const getPage = (path: string): Pages | null => {
  // Match the router order from specific to general
  const pages = {
    [Pages.Funnel]: isFunnelPage(path),
    [Pages.Result]: isResultPage(path),
    [Pages.LLP]: isLocalLandingPage(path),
    [Pages.LP]: isLandingPage(path),
    [Pages.IV24Funnel]: isIV24FunnelPage(path),
    [Pages.IV24Result]: isIV24ResultPage(path),
  };
  return (Object.keys(pages).find((key) => pages[key]) as Pages) ?? null;
};

export const scrollIntoViewByElement = (elementId?: string): void => {
  if (elementId) {
    const element = document.querySelector(elementId);
    element?.scrollIntoView();
  } else {
    window.scrollTo({ top: 0 });
  }
};
